// src/pages/DashboardPage.jsx
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { lmsAPI, resolveMedia, getToken } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ProgressRing from '../components/ui/ProgressRing';
import axios from 'axios';

const API_URL   = process.env.REACT_APP_API_URL   || 'http://localhost:5002/api/v1';
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
const SPA_URL   = process.env.REACT_APP_SPA_URL   || 'http://localhost:3000';

function resolveUser(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

// ── Animated counter ──────────────────────────────────────
function CountUp({ to, duration = 900 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!to) return;
    clearInterval(ref.current);
    let n = 0;
    const step = Math.max(1, Math.ceil(to / (duration / 16)));
    ref.current = setInterval(() => {
      n = Math.min(n + step, to);
      setVal(n);
      if (n >= to) clearInterval(ref.current);
    }, 16);
    return () => clearInterval(ref.current);
  }, [to]);
  return <>{val}</>;
}

function StatPill({ icon, value, label, color = 'var(--gold)', delay = '0s' }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', padding:'14px 18px', animation:`fade-up 0.5s ${delay} ease both`, transition:'all 0.2s', position:'relative', overflow:'hidden' }}
      onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(232,164,58,0.3)'}
      onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
      <div style={{ width:38, height:38, borderRadius:'var(--r-md)', background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0, border:`1px solid ${color}28` }}>
        <span style={{ fontSize:16 }}>{icon}</span>
      </div>
      <div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', fontWeight:700, color, lineHeight:1 }}><CountUp to={value}/></div>
        <div style={{ fontSize:'0.7rem', color:'var(--t-muted)', marginTop:2 }}>{label}</div>
      </div>
    </div>
  );
}

function CourseCard({ item, idx }) {
  const [hover, setHover] = useState(false);
  const pct    = item.lms_progress_pct || 0;
  const hasLMS = !!item.course_id && item.is_published;
  const img    = resolveMedia(item.media_url);
  return (
    <Link to={hasLMS ? `/course/${item.course_id}` : '#'}
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{ display:'block', borderRadius:'var(--r-xl)', overflow:'hidden', background:'var(--bg-2)', border:`1px solid ${hover?'rgba(232,164,58,0.4)':'var(--border)'}`, transition:'all 0.25s cubic-bezier(.4,0,.2,1)', transform:hover?'translateY(-4px)':'none', boxShadow:hover?'0 16px 40px rgba(0,0,0,0.4)':'none', textDecoration:'none', animation:`fade-up 0.5s ${idx*0.07}s ease both` }}>
      <div style={{ height:148, background:'var(--bg-3)', position:'relative', overflow:'hidden' }}>
        {img ? <img src={img} alt={item.program_title} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.6s ease', transform:hover?'scale(1.08)':'scale(1)' }} onError={e=>e.target.parentNode.style.background='var(--bg-3)'}/> : <div style={{ width:'100%', height:'100%', background:'linear-gradient(135deg, var(--bg-3) 0%, var(--bg-4) 100%)', display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ fontFamily:'var(--font-display)', fontSize:'2.5rem', opacity:0.15, fontWeight:700 }}>M</div></div>}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(8,10,13,0.9) 0%, rgba(8,10,13,0.2) 50%, transparent 100%)' }}/>
        <div style={{ position:'absolute', top:12, left:12, display:'flex', gap:5 }}>
          {item.cert_status==='issued'   && <span className="badge badge-green" style={{ fontSize:'0.58rem', backdropFilter:'blur(8px)', background:'rgba(62,207,142,0.2)' }}>🎓 Certified</span>}
          {item.cert_status==='eligible' && <span className="badge badge-gold"  style={{ fontSize:'0.58rem', backdropFilter:'blur(8px)', background:'rgba(232,164,58,0.2)' }}>⭐ Cert ready</span>}
          {!hasLMS && <span className="badge badge-dim" style={{ fontSize:'0.58rem', backdropFilter:'blur(8px)' }}>Coming soon</span>}
        </div>
        <div style={{ position:'absolute', bottom:12, right:12, display:'flex', alignItems:'center', gap:6, background:'rgba(8,10,13,0.8)', backdropFilter:'blur(12px)', borderRadius:24, padding:'5px 10px 5px 5px', border:'1px solid rgba(255,255,255,0.08)' }}>
          <ProgressRing pct={pct} size={26} stroke={3}/>
          <span style={{ fontSize:'0.7rem', fontWeight:600, color:pct===100?'var(--teal)':'var(--t-primary)' }}>{pct}%</span>
        </div>
      </div>
      <div style={{ padding:'16px 18px 18px' }}>
        <div style={{ fontSize:'0.62rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:5 }}>{item.cohort_name||'Self-paced'}</div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:'0.98rem', fontWeight:600, lineHeight:1.35, marginBottom:12, color:hover?'var(--gold)':'var(--t-primary)', transition:'color 0.2s' }}>{item.program_title}</div>
        <div className="progress-bar" style={{ marginBottom:8 }}>
          <div className="progress-fill" style={{ width:`${pct}%`, background:pct===100?'var(--teal)':'linear-gradient(90deg,var(--gold),var(--gold-2))' }}/>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.7rem', color:'var(--t-muted)' }}>
          <span>{item.completed_lessons||0}/{item.total_lessons||'—'} lessons</span>
          <span style={{ color:pct===0?'var(--gold)':pct===100?'var(--teal)':'var(--gold)', fontWeight:500 }}>{pct===0?'Start →':pct===100?'✓ Complete':'Continue →'}</span>
        </div>
      </div>
    </Link>
  );
}

function DiscoverCard({ program }) {
  const [hover, setHover] = useState(false);
  return (
    <a href={`${SPA_URL}/academy/programs/${program.slug}`} target="_blank" rel="noopener noreferrer"
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{ display:'flex', gap:14, padding:'14px 16px', borderRadius:'var(--r-lg)', background:hover?'var(--bg-3)':'var(--bg-2)', border:`1px solid ${hover?'rgba(232,164,58,0.28)':'var(--border)'}`, textDecoration:'none', transition:'all 0.18s', alignItems:'center', transform:hover?'translateX(4px)':'none' }}>
      <div style={{ width:50, height:50, borderRadius:'var(--r-md)', overflow:'hidden', background:'var(--bg-3)', flexShrink:0, border:'1px solid var(--border)' }}>
        {resolveMedia(program.media_url) ? <img src={resolveMedia(program.media_url)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'}/> : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontSize:'1.2rem', opacity:0.2 }}>M</div>}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:500, fontSize:'0.86rem', color:hover?'var(--gold)':'var(--t-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', transition:'color 0.15s' }}>{program.title||program.name}</div>
        <div style={{ fontSize:'0.7rem', color:'var(--t-muted)', marginTop:3, display:'flex', gap:8 }}>
          <span style={{ textTransform:'capitalize' }}>{program.type||program.program_type||'Program'}</span>
          {program.duration && <><span>·</span><span>{program.duration}</span></>}
        </div>
      </div>
      <div style={{ fontSize:'0.72rem', color:'var(--gold)', fontWeight:600, flexShrink:0, opacity:hover?1:0.6, transition:'opacity 0.15s' }}>Enroll →</div>
    </a>
  );
}

function NewsItem({ article }) {
  const [hover, setHover] = useState(false);
  return (
    <a href={`${SPA_URL}/news/${article.slug}`} target="_blank" rel="noopener noreferrer"
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{ display:'flex', gap:12, padding:'11px 0', borderBottom:'1px solid var(--border)', textDecoration:'none', alignItems:'flex-start', transition:'all 0.15s' }}>
      <div style={{ width:6, height:6, borderRadius:'50%', background:hover?'var(--gold)':'var(--t-muted)', marginTop:6, flexShrink:0, transition:'background 0.15s' }}/>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'0.83rem', fontWeight:500, color:hover?'var(--gold)':'var(--t-primary)', lineHeight:1.4, transition:'color 0.15s' }}>{article.title}</div>
        <div style={{ fontSize:'0.68rem', color:'var(--t-muted)', marginTop:3, display:'flex', gap:8 }}>
          <span style={{ textTransform:'capitalize' }}>{article.content_type}</span>
          {article.published_at && <><span>·</span><span>{new Date(article.published_at).toLocaleDateString('en-RW',{day:'numeric',month:'short'})}</span></>}
        </div>
      </div>
    </a>
  );
}

function SectionHeader({ title, count, badge, action }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
      <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.05rem', fontWeight:600, whiteSpace:'nowrap' }}>{title}</h2>
      {count !== undefined && <span style={{ fontSize:'0.7rem', color:'var(--t-muted)' }}>{count}</span>}
      {badge}
      <div style={{ height:'1px', flex:1, background:'linear-gradient(90deg, var(--border) 0%, transparent 100%)' }}/>
      {action}
    </div>
  );
}

function SideWidget({ title, children, topRight, accent }) {
  return (
    <div style={{ background:'var(--bg-2)', border:`1px solid ${accent?'rgba(232,164,58,0.2)':'var(--border)'}`, borderRadius:'var(--r-xl)', overflow:'hidden', ...(accent?{background:'linear-gradient(160deg, rgba(232,164,58,0.05) 0%, var(--bg-2) 60%)'}:{}) }}>
      <div style={{ padding:'13px 18px', borderBottom:`1px solid ${accent?'rgba(232,164,58,0.12)':'var(--border)'}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.9rem', color:accent?'var(--gold)':'var(--t-primary)' }}>{title}</div>
        {topRight}
      </div>
      <div style={{ padding:'16px 18px' }}>{children}</div>
    </div>
  );
}

function Milestone({ icon, label, val, target }) {
  const pct  = Math.min(100, Math.round(val / target * 100));
  const done = val >= target;
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
        <span style={{ fontSize:'0.78rem', color:done?'var(--teal)':'var(--t-secondary)', display:'flex', alignItems:'center', gap:6 }}><span style={{ fontSize:14 }}>{icon}</span>{label}</span>
        <span style={{ fontSize:'0.72rem', fontWeight:600, color:done?'var(--teal)':'var(--t-muted)', fontFamily:'var(--font-mono)' }}>{val}/{target}</span>
      </div>
      <div style={{ height:'3px', background:'rgba(255,255,255,0.06)', borderRadius:2, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:done?'var(--teal)':'linear-gradient(90deg,var(--gold),var(--gold-2))', borderRadius:2, transition:'width 1s ease' }}/>
      </div>
    </div>
  );
}

// ── MAIN DASHBOARD ────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuth();
  const [items,    setItems]    = useState([]);
  const [news,     setNews]     = useState([]);
  const [discover, setDiscover] = useState([]);
  const [nextUp,   setNextUp]   = useState(null);
  const [loading,  setLoading]  = useState(true);

  const QUOTES = [
    { text:"The expert in anything was once a beginner.", author:"Helen Hayes" },
    { text:"Learning is not attained by chance. It must be sought with ardor.", author:"Abigail Adams" },
    { text:"Education is the passport to the future.", author:"Malcolm X" },
    { text:"The beautiful thing about learning is that no one can take it away from you.", author:"B.B. King" },
    { text:"An investment in knowledge pays the best interest.", author:"Benjamin Franklin" },
    { text:"Live as if you were to die tomorrow. Learn as if you were to live forever.", author:"Gandhi" },
    { text:"The capacity to learn is a gift; the ability to learn is a skill; the willingness to learn is a choice.", author:"Brian Herbert" },
  ];
  const [quote] = useState(() => QUOTES[Math.floor(Math.random()*QUOTES.length)]);

  useEffect(() => {
    const token = getToken();
    const h = token ? { Authorization:`Bearer ${token}` } : {};
    Promise.all([
      lmsAPI.myDashboard(),
      axios.get(`${API_URL}/articles?limit=5&status=published`, { headers:h }).catch(()=>({ data:{ data:[] } })),
      axios.get(`${API_URL}/programs?limit=8`, { headers:h }).catch(()=>({ data:{ data:[] } })),
    ]).then(([dash, art, prog]) => {
      const enrolled = dash.data.data || [];
      setItems(enrolled);
      setNews((art.data.data||[]).slice(0,5));
      const enrolledIds = new Set(enrolled.map(e=>e.program_id));
      setDiscover((prog.data.data||[]).filter(p=>!enrolledIds.has(p.id)).slice(0,4));
      const active = enrolled.find(i=>i.course_id && i.is_published && (i.lms_progress_pct||0)<100);
      if (active) setNextUp(active);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const total        = items.length;
  const inProgress   = items.filter(i=>(i.lms_progress_pct||0)>0&&(i.lms_progress_pct||0)<100);
  const notStarted   = items.filter(i=>!(i.lms_progress_pct||0));
  const completed    = items.filter(i=>(i.lms_progress_pct||0)>=100||i.cert_status==='issued');
  const certCount    = items.filter(i=>i.cert_status==='issued').length;
  const lessonsDone  = items.reduce((a,i)=>a+(parseInt(i.completed_lessons)||0),0);
  const lessonsTotal = items.reduce((a,i)=>a+(parseInt(i.total_lessons)||0),0);
  const avgPct       = total ? Math.round(items.reduce((a,i)=>a+(i.lms_progress_pct||0),0)/total) : 0;
  const hour         = new Date().getHours();
  const greeting     = hour<5?'Still at it':hour<12?'Good morning':hour<17?'Good afternoon':'Good evening';
  const firstName    = user?.name?.split(' ')[0] || 'Learner';

  const avatarSrc = resolveUser(user?.avatar_url);
  const coverSrc  = resolveUser(user?.cover_url);

  return (
    <div style={{ paddingTop:'var(--nav-h)', minHeight:'100vh', background:'var(--bg-0)' }}>

      {/* ── HERO ── */}
      <div style={{ position:'relative', overflow:'hidden', background:'var(--bg-1)', borderBottom:'1px solid var(--border)', padding:'clamp(36px,5vw,64px) 0' }}>

        {/* Cover photo background */}
        {coverSrc && (
          <div style={{ position:'absolute', inset:0, zIndex:0 }}>
            <img src={coverSrc} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', filter:'brightness(0.18) saturate(0.6)' }}/>
          </div>
        )}

        {/* Background decoration */}
        <div style={{ position:'absolute', top:-60, right:-60, width:380, height:380, borderRadius:'50%', background:'radial-gradient(circle, rgba(232,164,58,0.07) 0%, transparent 65%)', pointerEvents:'none', zIndex:1 }}/>
        <div style={{ position:'absolute', bottom:-80, left:'30%', width:280, height:280, borderRadius:'50%', background:'radial-gradient(circle, rgba(78,156,245,0.04) 0%, transparent 65%)', pointerEvents:'none', zIndex:1 }}/>
        <div style={{ position:'absolute', top:0, left:0, right:0, height:'1px', background:'linear-gradient(90deg,transparent,rgba(232,164,58,0.3),transparent)', zIndex:1 }}/>

        <div className="container" style={{ position:'relative', zIndex:2 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:40, alignItems:'center' }}>

            {/* Left: avatar + greeting */}
            <div>
              <div className="label fade-up" style={{ marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--gold)', display:'inline-block', animation:'glow-pulse 2s infinite' }}/>
                MilleHills Academy
              </div>

              {/* Avatar */}
              {avatarSrc && (
                <div style={{ width:60, height:60, borderRadius:'50%', overflow:'hidden', border:'2px solid var(--gold)', marginBottom:16, boxShadow:'0 0 24px rgba(232,164,58,0.35)', flexShrink:0 }}>
                  <img src={avatarSrc} alt={user?.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                </div>
              )}

              <h1 className="fade-up-1" style={{ fontFamily:'var(--font-display)', fontSize:'clamp(1.8rem,4vw,3rem)', fontWeight:600, lineHeight:1.1, marginBottom:14 }}>
                {greeting},<br/>
                <span style={{ color:'var(--gold)', fontStyle:'italic' }}>{firstName}.</span>
              </h1>
              <p className="fade-up-2" style={{ color:'var(--t-secondary)', fontSize:'0.88rem', fontStyle:'italic', maxWidth:420, lineHeight:1.8, borderLeft:'2px solid rgba(232,164,58,0.3)', paddingLeft:14 }}>
                "{quote.text}"
                <span style={{ display:'block', fontStyle:'normal', fontSize:'0.76rem', color:'var(--t-muted)', marginTop:5 }}>— {quote.author}</span>
              </p>
            </div>

            {/* Right: progress orbs */}
            {!loading && total > 0 && (
              <div className="fade-up-2" style={{ display:'flex', gap:20, alignItems:'center', flexShrink:0 }}>
                {[
                  { pct:avgPct, label:'Overall', sub:total+' courses', color:'var(--gold)', size:72 },
                  { pct:lessonsTotal?Math.round(lessonsDone/lessonsTotal*100):0, label:'Lessons', sub:lessonsDone+'/'+lessonsTotal, color:'var(--teal)', size:72 },
                  ...(certCount>0?[{ pct:100, label:'Certs', sub:certCount+' earned', color:'var(--blue)', size:72 }]:[]),
                ].map((r,i) => (
                  <div key={r.label} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:7, animation:`fade-up 0.5s ${0.2+i*0.1}s ease both` }}>
                    <div style={{ position:'relative', width:r.size, height:r.size }}>
                      <div style={{ position:'absolute', inset:-3, borderRadius:'50%', background:`radial-gradient(circle, ${r.color}15 0%, transparent 70%)` }}/>
                      <ProgressRing pct={r.pct} size={r.size} stroke={5} color={r.color}/>
                      <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontWeight:700, fontSize:r.size*0.21, color:r.color }}>{r.pct}%</div>
                    </div>
                    <div style={{ fontSize:'0.75rem', fontWeight:500, color:'var(--t-secondary)', textAlign:'center' }}>{r.label}</div>
                    <div style={{ fontSize:'0.65rem', color:'var(--t-muted)', textAlign:'center' }}>{r.sub}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="container" style={{ padding:'clamp(24px,4vw,52px) clamp(16px,4vw,52px)' }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:100, color:'var(--t-muted)' }}>
            <div style={{ width:40, height:40, borderRadius:12, background:'var(--gold)', display:'inline-flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontWeight:700, fontSize:20, color:'#080a0d', animation:'float 2s ease infinite', marginBottom:16 }}>M</div>
            <div style={{ fontSize:'0.88rem' }}>Loading your learning space…</div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 296px', gap:28, alignItems:'start' }}>

            {/* ── LEFT ── */}
            <div style={{ display:'flex', flexDirection:'column', gap:36 }}>

              {total === 0 && (
                <div style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-2xl)', padding:'52px 40px', textAlign:'center', position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%', background:'radial-gradient(circle, rgba(232,164,58,0.08) 0%, transparent 70%)', pointerEvents:'none' }}/>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:'3rem', opacity:0.15, fontWeight:700, marginBottom:20, animation:'float 3s ease infinite' }}>M</div>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', fontWeight:600, marginBottom:10 }}>Your journey starts here</div>
                  <div style={{ color:'var(--t-muted)', fontSize:'0.88rem', marginBottom:32, maxWidth:340, margin:'0 auto 32px', lineHeight:1.7 }}>
                    Enroll in a program at millehills.rw and come back here to learn, track progress, and earn certificates.
                  </div>
                  <a href={`${SPA_URL}/academy`} className="btn btn-primary" style={{ padding:'13px 36px', fontWeight:600, fontSize:'0.95rem' }}>Browse Programs →</a>
                </div>
              )}

              {total > 0 && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
                  <StatPill icon="◐" value={total}              label="Enrolled"     color="var(--gold)"   delay="0s"/>
                  <StatPill icon="⟳" value={inProgress.length} label="In progress"  color="var(--blue)"   delay="0.06s"/>
                  <StatPill icon="✓" value={completed.length}  label="Completed"    color="var(--teal)"   delay="0.12s"/>
                  <StatPill icon="◈" value={lessonsDone}       label="Lessons done" color="var(--purple)" delay="0.18s"/>
                </div>
              )}

              {nextUp && (
                <div>
                  <SectionHeader title="Pick up where you left off"/>
                  <Link to={`/course/${nextUp.course_id}`}
                    style={{ display:'block', textDecoration:'none', borderRadius:'var(--r-2xl)', overflow:'hidden', position:'relative', transition:'all 0.25s ease' }}
                    onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='0 20px 50px rgba(0,0,0,0.5)';}}
                    onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='none';}}>
                    {resolveMedia(nextUp.media_url) && (
                      <div style={{ position:'absolute', inset:0, zIndex:0 }}>
                        <img src={resolveMedia(nextUp.media_url)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', filter:'brightness(0.25) saturate(0.8)' }} onError={e=>e.target.parentNode.style.display='none'}/>
                      </div>
                    )}
                    <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg, rgba(8,10,13,0.95) 0%, rgba(8,10,13,0.7) 100%)', zIndex:1 }}/>
                    <div style={{ position:'absolute', top:0, left:0, right:0, height:'1px', background:'linear-gradient(90deg,transparent,rgba(232,164,58,0.5),transparent)', zIndex:2 }}/>
                    <div style={{ position:'relative', zIndex:2, display:'flex', gap:24, padding:'26px 28px', alignItems:'center' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'0.65rem', color:'var(--gold)', letterSpacing:'0.14em', textTransform:'uppercase', marginBottom:6 }}>Continue learning</div>
                        <div style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:600, marginBottom:14, lineHeight:1.2 }}>{nextUp.program_title}</div>
                        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:10 }}>
                          <div className="progress-bar" style={{ flex:1, height:'4px' }}>
                            <div className="progress-fill" style={{ width:`${nextUp.lms_progress_pct||0}%` }}/>
                          </div>
                          <span style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--gold)', fontFamily:'var(--font-display)', flexShrink:0 }}>{nextUp.lms_progress_pct||0}%</span>
                        </div>
                        <div style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>{nextUp.completed_lessons||0} of {nextUp.total_lessons||'—'} lessons · {nextUp.cohort_name||'Self-paced'}</div>
                      </div>
                      <div className="btn btn-primary" style={{ padding:'14px 28px', fontWeight:700, fontSize:'0.95rem', flexShrink:0, boxShadow:'0 0 30px rgba(232,164,58,0.3)' }}>Resume →</div>
                    </div>
                  </Link>
                </div>
              )}

              {inProgress.length > 0 && (
                <div>
                  <SectionHeader title="In Progress" count={inProgress.length}/>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 }}>
                    {inProgress.map((item,i)=><CourseCard key={item.enrollment_id} item={item} idx={i}/>)}
                  </div>
                </div>
              )}

              {notStarted.length > 0 && (
                <div>
                  <SectionHeader title="Not Started Yet" count={notStarted.length}/>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 }}>
                    {notStarted.map((item,i)=><CourseCard key={item.enrollment_id} item={item} idx={i}/>)}
                  </div>
                </div>
              )}

              {completed.length > 0 && (
                <div>
                  <SectionHeader title="Completed" badge={<span className="badge badge-green" style={{ fontSize:'0.6rem' }}>✓ {completed.length}</span>}/>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 }}>
                    {completed.map((item,i)=><CourseCard key={item.enrollment_id} item={item} idx={i}/>)}
                  </div>
                </div>
              )}

              {discover.length > 0 && (
                <div>
                  <SectionHeader title="Explore More Programs"
                    action={<a href={`${SPA_URL}/academy`} target="_blank" rel="noopener noreferrer" style={{ fontSize:'0.75rem', color:'var(--gold)', textDecoration:'none', fontWeight:600 }}>All →</a>}/>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {discover.map(p=><DiscoverCard key={p.id} program={p}/>)}
                  </div>
                </div>
              )}
            </div>

            {/* ── RIGHT SIDEBAR ── */}
            <div style={{ display:'flex', flexDirection:'column', gap:16, position:'sticky', top:'calc(var(--nav-h) + 20px)' }}>

              {items.some(i=>i.cert_status==='eligible') && (
                <div style={{ background:'linear-gradient(135deg, rgba(62,207,142,0.12) 0%, rgba(62,207,142,0.04) 100%)', border:'1px solid rgba(62,207,142,0.3)', borderRadius:'var(--r-xl)', padding:18 }}>
                  <div style={{ fontSize:'0.65rem', color:'var(--teal)', letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:8, fontWeight:600 }}>🎓 Ready to certify!</div>
                  {items.filter(i=>i.cert_status==='eligible').map(i=>(
                    <div key={i.enrollment_id} style={{ marginBottom:8 }}>
                      <div style={{ fontSize:'0.88rem', fontWeight:600, marginBottom:3 }}>{i.program_title}</div>
                      <div style={{ fontSize:'0.76rem', color:'var(--t-muted)', lineHeight:1.6 }}>Your instructor will review and issue your certificate soon.</div>
                    </div>
                  ))}
                </div>
              )}

              <SideWidget title="Quick Actions">
                {[
                  { label:'Browse Programs',   icon:'◈', href:`${SPA_URL}/academy`,                   color:'var(--gold)' },
                  { label:'My Profile',         icon:'◉', href:`${SPA_URL}/dashboard`,                color:'var(--blue)' },
                  { label:'Services',           icon:'◧', href:`${SPA_URL}/services`,                 color:'var(--teal)' },
                  { label:'Open Internships',   icon:'◎', href:`${SPA_URL}/academy?tab=internships`,  color:'var(--purple)' },
                  { label:'Contact MilleHills', icon:'◌', href:`${SPA_URL}/contact`,                  color:'var(--t-muted)' },
                ].map(a => (
                  <a key={a.label} href={a.href} target="_blank" rel="noopener noreferrer"
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid var(--border)', textDecoration:'none', transition:'all 0.15s' }}
                    onMouseEnter={e=>{e.currentTarget.style.paddingLeft='8px';}}
                    onMouseLeave={e=>{e.currentTarget.style.paddingLeft='0';}}>
                    <span style={{ fontSize:'0.9rem', color:a.color, width:18 }}>{a.icon}</span>
                    <span style={{ fontSize:'0.82rem', color:'var(--t-secondary)', flex:1 }}>{a.label}</span>
                    <span style={{ fontSize:'0.62rem', color:'var(--t-muted)' }}>↗</span>
                  </a>
                ))}
              </SideWidget>

              {total > 0 && (
                <SideWidget title="Milestones" accent>
                  <Milestone icon="📚" label="Lessons completed"  val={lessonsDone} target={50}/>
                  <Milestone icon="🎯" label="Programs enrolled"  val={total}       target={5}/>
                  <Milestone icon="🏆" label="Certificates earned" val={certCount}  target={3}/>
                  {avgPct >= 50 && (
                    <div style={{ marginTop:12, padding:'10px 12px', background:'rgba(232,164,58,0.06)', borderRadius:'var(--r-md)', border:'1px solid var(--gold-border)', fontSize:'0.78rem', color:'var(--t-secondary)', lineHeight:1.6 }}>
                      ✨ {avgPct>=80?'Outstanding progress, ':avgPct>=50?'Great momentum, ':''}{firstName}! Keep going.
                    </div>
                  )}
                </SideWidget>
              )}

              {news.length > 0 && (
                <SideWidget title="From the Ecosystem"
                  topRight={<a href={`${SPA_URL}/news`} target="_blank" rel="noopener noreferrer" style={{ fontSize:'0.7rem', color:'var(--gold)', textDecoration:'none', fontWeight:600 }}>All news →</a>}>
                  {news.map(a=><NewsItem key={a.id} article={a}/>)}
                </SideWidget>
              )}

              <a href={`${SPA_URL}/academy?tab=internships`} target="_blank" rel="noopener noreferrer"
                style={{ display:'block', background:'linear-gradient(135deg, rgba(232,164,58,0.08) 0%, rgba(78,156,245,0.04) 100%)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-xl)', padding:18, textDecoration:'none', transition:'all 0.2s' }}
                onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 8px 30px rgba(232,164,58,0.12)';}}
                onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='none';}}>
                <div style={{ fontSize:'0.65rem', color:'var(--gold)', letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:7, fontWeight:600 }}>💼 Opportunities</div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:'0.98rem', fontWeight:600, marginBottom:5 }}>Internships at MilleHills</div>
                <div style={{ fontSize:'0.78rem', color:'var(--t-muted)', lineHeight:1.65, marginBottom:12 }}>Turn your learning into real experience. Apply for open positions.</div>
                <div style={{ fontSize:'0.82rem', color:'var(--gold)', fontWeight:600 }}>View open positions →</div>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
